import {DatabaseCode, toDatabaseCode} from "./database-code";

/**
 * A message's type, mirroring the server's `MessageType`. `code` is the database code, which is
 * also the type's JSON value -- what `Message.messageType` holds and the message endpoints expect.
 */
export type MessageType = DatabaseCode & {
  /** Whether a person can choose this type for a message they write. */
  selectable: boolean;
  /**
   * Whether choosing this type needs elevated access. The host decides who has it -- super admins
   * for now, a permission later.
   */
  restricted: boolean;
  /**
   * Whether this is a dedicated kind of message rather than a general comment, so it is not offered
   * when commenting on a transport or shift. Site notes and driver transfers each mean something
   * specific; filed as a ticket comment they would read as something they are not.
   */
  dedicated: boolean;
};

const type = (
  code: string,
  key: string,
  {selectable = true, restricted = false, dedicated = false}: {
    selectable?: boolean;
    restricted?: boolean;
    dedicated?: boolean
  } = {}
): MessageType => ({
  code,
  selectable,
  restricted,
  dedicated,
  displayValue: {
    id: `common.entity.message.type.${key}`,
    defaultMessage: code,
  },
});

export const messageType = {
  comment: type("Comment", "comment"),
  internal: type("Internal", "internal", {restricted: true}),

  dispatch: type("Dispatch", "dispatch"),
  siteNotes: type("Site Notes", "sitenotes", {dedicated: true}),
  chainUp: type("Chain Up", "chainup"),
  authorization: type("Authorization", "authorization"),
  maintenance: type("Maintenance", "maintenance"),

  discard: type("Discard", "discard"),
  discardEnteredTwice: type("Discard - Entered Twice", "discardenteredtwice"),

  driverTransferPickup: type("Driver Transfer Pickup", "drivertransferpickup", {dedicated: true}),
  driverTransferDelivery: type("Driver Transfer Delivery", "drivertransferdelivery", {dedicated: true}),

  wrongDriverUsed: type("Wrong Driver Used", "wrongdriverused"),
  wrongTruckUsed: type("Wrong Truck Used", "wrongtruckused"),
  wrongTrailerUsed: type("Wrong Trailer Used", "wrongtrailerused"),
  wrongQuantityEntered: type("Wrong Quantity Entered", "wrongquantityentered"),
  wrongSiteUsed: type("Wrong Site Used", "wrongsiteused"),
  wrongContainerUsed: type("Wrong Container Used", "wrongcontainerused"),
  wrongStartTime: type("Wrong Start Time", "wrongstarttime"),
  wrongEndTime: type("Wrong End Time", "wrongendtime"),

  restrictionHunting: type("Restriction - Hunting", "restrictionhunting"),
  restrictionWeather: type("Restriction - Weather", "restrictionweather"),
  restrictionRoad: type("Restriction - Road", "restrictionroad"),

  /** Person notices. No longer created; kept so existing rows still resolve. */
  userNotice: type("User Notice", "usernotice", {selectable: false}),
};

export const messageTypes: MessageType[] = Object.values(messageType);

/** Object types whose comments are general comments, so dedicated types are not offered on them. */
const generalCommentObjectTypes = ["TRANSPORT", "SHIFT"];

/**
 * The types a person may choose, in order. Restricted ones only when `includeRestricted`; dedicated
 * ones are left out when the message is on a transport or shift.
 */
export const messageTypeChoices = (includeRestricted: boolean, objectType?: string): MessageType[] => {
  const generalCommentsOnly = objectType !== undefined && generalCommentObjectTypes.includes(objectType);

  return messageTypes.filter((t) =>
    t.selectable && (includeRestricted || !t.restricted) && !(generalCommentsOnly && t.dedicated)
  );
};

/** The types anyone may choose. A host that knows the person may use restricted types asks for them. */
export const selectableMessageTypes: MessageType[] = messageTypeChoices(false);

/**
 * The type for a stored code. Anything the server does not know -- including the legacy "COMMENT"
 * some older clients sent -- reads as Comment, as the server reads it.
 */
export const toMessageType = (code: string | undefined): MessageType =>
  code ? toDatabaseCode(code, messageTypes, messageType.comment) : messageType.comment;
